import { Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

import { Dimens } from '~themes';

import ErrorType from './errorType';
import ActionButton from '../buttons/actionButton';

interface ErrorViewProps {
	type: ErrorType;
	/** Optional override for the description text below the heading. */
	description?: string;
	/** Callback for the action button. When omitted, no button is rendered. */
	onAction?: () => void;
}

const headingByType: Record<ErrorType, string> = {
	[ErrorType.NotFound]: '404',
	[ErrorType.NotAuthorized]: '401',
	[ErrorType.Forbidden]: '403',
	[ErrorType.Other]: 'Oops!',
};

/**
 * Reusable error presentation component.
 *
 * Renders a large status heading, a description, and an optional action button.
 * Centres itself vertically and horizontally within whatever container it is
 * placed in (uses both `flex: 1` and `height: 100%` for maximum compatibility).
 */
const ErrorView = ({ type, description, onAction }: ErrorViewProps) => {
	const { t } = useTranslation();
	const { palette } = useTheme();

	const resolvedDescription = description ?? defaultDescriptionByType(type, t);
	const actionLabel = defaultActionLabelByType(type, t);

	return (
		<Stack
			direction='column'
			spacing={Dimens.spacing2xl}
			sx={{
				alignItems: 'center',
				justifyContent: 'center',
				flex: 1,
				height: '100%',
				p: 4,
			}}
		>
			<Typography variant='heading2xl' color={palette.textSecondary.main}>
				{headingByType[type]}
			</Typography>

			{resolvedDescription && (
				<Typography
					variant='paragraph'
					color={palette.textSecondary.main}
					sx={{ textAlign: 'center' }}
				>
					{resolvedDescription}
				</Typography>
			)}

			{onAction && <ActionButton label={actionLabel} onClick={onAction} />}
		</Stack>
	);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function defaultDescriptionByType(type: ErrorType, t: any): string {
	switch (type) {
		case ErrorType.NotFound:
			return t('pageNotFound', {
				ns: 'errors',
				defaultValue: 'The page you are looking for could not be found.',
			});
		case ErrorType.NotAuthorized:
			return t('unauthorized', { ns: 'errors' });
		case ErrorType.Forbidden:
			return t('unauthorized', { ns: 'errors' });
		case ErrorType.Other:
			return t('somethingWentWrong', { ns: 'errors' });
		default:
			throw new Error(`Unhandled ErrorType: ${type}`);
	}
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function defaultActionLabelByType(type: ErrorType, t: any): string {
	switch (type) {
		case ErrorType.NotAuthorized:
		case ErrorType.Forbidden:
			return t('navigateToDashboard', { ns: 'nav' });
		case ErrorType.NotFound:
		case ErrorType.Other:
			return t('tryAgain', { ns: 'actions' });
		default:
			throw new Error(`Unhandled ErrorType: ${type}`);
	}
}

export default ErrorView;
